import { Alert, Badge, Button, Card, DataTable, FormField, FormSurface, Input, Module, ModuleBody, ModuleGrid, ModuleHead, Select } from "@vjal/ui";
import { useMemo, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ApiClientError } from "../services/api-client";
import {
  useCoachingProfile,
  usePlatformOrganizationCreateMutation,
  usePlatformOrganizationsQuery
} from "../hooks/useCoachingQueries";
import type { CoachingProfileDto } from "../services/coaching-dtos";
import { isCoachingRole } from "../domain/roles";
import { switchCoachingOrganization } from "../services/coaching-org-switch";

export function PlatformOrganizationsSurface() {
  const profile = useCoachingProfile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const organizations = usePlatformOrganizationsQuery(profile.data?.isPlatformAdmin === true);
  const create = usePlatformOrganizationCreateMutation();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"name" | "created_at">("created_at");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<"admin" | "super_admin">("admin");
  const [copied, setCopied] = useState(false);
  const [lastCreated, setLastCreated] = useState<Awaited<ReturnType<typeof create.mutateAsync>> | null>(null);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return [...(organizations.data ?? [])]
      .filter((organization) => !query || organization.name.toLowerCase().includes(query))
      .sort((left, right) => sort === "name"
        ? left.name.localeCompare(right.name)
        : right.created_at.localeCompare(left.created_at));
  }, [organizations.data, search, sort]);

  if (profile.isLoading || organizations.isLoading) {
    return <Module span={12}><ModuleBody><Alert variant="info">Loading organisations and coaching visibility.</Alert></ModuleBody></Module>;
  }
  if (!profile.data?.isPlatformAdmin) {
    return <Module span={12}><ModuleBody><Alert variant="error">Platform administrator access is required.</Alert></ModuleBody></Module>;
  }

  const submit = () => {
    create.mutate({ name, firstAdmin: { email, firstName: firstName || undefined, lastName: lastName || undefined }, role }, {
      onSuccess: (result) => {
        setLastCreated(result);
        setName("");
        setEmail("");
        setFirstName("");
        setLastName("");
      }
    });
  };
  const copyInviteUrl = async () => {
    if (!lastCreated) return;
    await navigator.clipboard?.writeText(lastCreated.inviteUrl);
    setCopied(true);
  };
  const errorMessage = create.error instanceof ApiClientError ? create.error.message : create.error ? "Organisation could not be created. Try again." : undefined;

  return (
    <ModuleGrid className="col-span-12">
      <Module span={12}>
        <ModuleHead title="Organisations" subtitle="Platform-wide Coaching organisations, counts, and grant visibility." />
        <ModuleBody>
          <div className="flex flex-wrap items-end gap-3">
            <label className="min-w-60 flex-1 text-sm font-medium text-ink-secondary">Search organisations
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name" aria-label="Search organisations" />
            </label>
            <Select label="Sort" value={sort} onChange={(event) => setSort(event.target.value as "name" | "created_at")} aria-label="Sort organisations">
              <option value="created_at">Newest</option><option value="name">Name</option>
            </Select>
          </div>
          {organizations.isError ? <Alert variant="error" className="mt-4">Organisation visibility is unavailable. {organizations.error instanceof ApiClientError ? organizations.error.message : "Try again later."}</Alert> : null}
          {!organizations.isError && filtered.length === 0 ? <Alert variant="info" className="mt-4">No Coaching organisations match this search.</Alert> : null}
          {filtered.length > 0 ? <DataTable
            caption="Coaching organisations"
            columns={organizationColumns(queryClient, profile.data, navigate)}
            data={filtered.map((organization) => ({ ...organization, id: organization.orgId, created: new Date(organization.created_at).toLocaleDateString(), ...organization.counts }))}
          /> : null}
        </ModuleBody>
      </Module>
      <Module span={6}>
        <FormSurface title="Create organisation" submitLabel="Create organisation" loading={create.isPending} onSubmit={submit} error={errorMessage}>
          <FormField label="Organisation name" hint="Use the legal or operational name shown to staff.">
            {({ id }) => <Input id={id} value={name} onChange={(event) => setName(event.target.value)} required />}
          </FormField>
          <FormField label="First admin email" hint="No invitation email is sent automatically; the response includes a sign-in link.">
            {({ id }) => <Input id={id} type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />}
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="First name">{({ id }) => <Input id={id} value={firstName} onChange={(event) => setFirstName(event.target.value)} />}</FormField>
            <FormField label="Last name">{({ id }) => <Input id={id} value={lastName} onChange={(event) => setLastName(event.target.value)} />}</FormField>
          </div>
          <Select label="Coaching role" value={role} onChange={(event) => setRole(event.target.value as "admin" | "super_admin")}>
            <option value="admin">Admin</option><option value="super_admin">Super admin</option>
          </Select>
        </FormSurface>
      </Module>
      <Module span={6}>
        <Card className="h-full p-6">
          <h2 className="m-0 font-display text-h3 text-strong">Creation status</h2>
          {!lastCreated ? <p className="mt-3 text-sm text-ink-muted">Create an organisation to see honest invite delivery status and the copyable sign-in link.</p> : <div className="mt-4 space-y-3">
            <p className="m-0 break-all text-sm text-ink-secondary">Sign-in link: <a href={lastCreated.inviteUrl}>{lastCreated.inviteUrl}</a></p>
            <div className="flex flex-wrap gap-2"><Button type="button" variant="secondary" onClick={copyInviteUrl}>{copied ? "Copied" : "Copy sign-in link"}</Button><Button type="button" onClick={() => switchCoachingOrganization({ membership: { orgId: lastCreated.orgId, orgName: lastCreated.name, coachingRole: "super_admin" }, queryClient, profile: profile.data, navigate })}>Switch to it</Button></div>
          </div>}
        </Card>
      </Module>
    </ModuleGrid>
  );
}

export function organizationColumns(queryClient: QueryClient, profile: CoachingProfileDto | undefined, navigate: (path: string) => void) {
  return [
    {
      key: "name",
      header: "Organisation",
      render: (row: Record<string, unknown>) => {
        const coachingRole = typeof row.coachingRole === "string" && isCoachingRole(row.coachingRole) ? row.coachingRole : null;
        if (!coachingRole) return <span className="font-medium" title="You have no Coaching role in this organisation">{String(row.name)}</span>;
        return <button type="button" className="font-medium underline" onClick={() => void switchCoachingOrganization({
          membership: { orgId: String(row.id), orgName: String(row.name), coachingRole },
          queryClient,
          profile,
          navigate,
        })}>{String(row.name)}</button>;
      }
    },
    { key: "created", header: "Created" },
    { key: "companies", header: "Companies" },
    { key: "cohorts", header: "Cohorts" },
    { key: "activeParticipants", header: "Active participants" },
    { key: "staffHolders", header: "Staff holders" },
    { key: "coachingRole", header: "Your Coaching role", render: (row: Record<string, unknown>) => <Badge variant="default">{String(row.coachingRole ?? "—")}</Badge> }
  ];
}
